import { CallPut, Trade, TradeDirection } from './types';
import { getTradeDirectionFromTradeSide } from './tradeSide';
import { normalizeCallPut } from './normalizeCallPut';

export { normalizeCallPut } from './normalizeCallPut';

export enum OptionFlowRowSentiment {
  Bullish = 'Bullish',
  Bearish = 'Bearish',
  Neutral = 'Neutral',
}

export enum OptionFlowStrategyPosition {
  Long = 'Long',
  Short = 'Short',
  Neutral = 'Neutral',
}

type SentimentTrade = Partial<Pick<Trade, 'cp' | 'tradeDirection' | 'tradeSide'>>;

export const getOptionFlowSideCode = (
  trade?: Pick<SentimentTrade, 'tradeDirection' | 'tradeSide'> | null,
): TradeDirection | '' => {
  if (trade?.tradeDirection) {
    return trade.tradeDirection;
  }

  return getTradeDirectionFromTradeSide(trade?.tradeSide) || '';
};

export const getOptionFlowRowSentiment = (
  trade?: SentimentTrade | null,
): OptionFlowRowSentiment => {
  const cp = normalizeCallPut(trade?.cp);
  const sideCode = getOptionFlowSideCode(trade);

  if (!cp || sideCode === TradeDirection.Mid || !sideCode) {
    return OptionFlowRowSentiment.Neutral;
  }

  const isAskSide = sideCode === TradeDirection.Ask || sideCode === TradeDirection.AboveAsk;
  const isBidSide = sideCode === TradeDirection.Bid || sideCode === TradeDirection.BelowBid;

  if (isAskSide) {
    return cp === CallPut.Call ? OptionFlowRowSentiment.Bullish : OptionFlowRowSentiment.Bearish;
  }

  if (isBidSide) {
    return cp === CallPut.Call ? OptionFlowRowSentiment.Bearish : OptionFlowRowSentiment.Bullish;
  }

  return OptionFlowRowSentiment.Neutral;
};

export const getOptionFlowStrategyPosition = (
  trade?: Pick<SentimentTrade, 'tradeDirection' | 'tradeSide'> | null,
): OptionFlowStrategyPosition => {
  const sideCode = getOptionFlowSideCode(trade);

  if (sideCode === TradeDirection.Ask || sideCode === TradeDirection.AboveAsk) {
    return OptionFlowStrategyPosition.Long;
  }

  if (sideCode === TradeDirection.Bid || sideCode === TradeDirection.BelowBid) {
    return OptionFlowStrategyPosition.Short;
  }

  return OptionFlowStrategyPosition.Neutral;
};

export const getOptionFlowStrategyPositionLabel = (
  trade?: Pick<SentimentTrade, 'tradeDirection' | 'tradeSide'> | null,
): string => {
  const position = getOptionFlowStrategyPosition(trade);

  return position === OptionFlowStrategyPosition.Neutral ? 'No Direction' : position;
};

export const getOptionFlowRowSentimentClassName = (trade?: SentimentTrade | null): string => {
  const sentiment = getOptionFlowRowSentiment(trade);

  switch (sentiment) {
    case OptionFlowRowSentiment.Bullish:
      return 'text-call-text';
    case OptionFlowRowSentiment.Bearish:
      return 'text-put-text';
    case OptionFlowRowSentiment.Neutral:
    default:
      return 'text-palette-black-7';
  }
};

export const getOptionFlowRowSentimentShortLabel = (trade?: SentimentTrade | null): string => {
  const sentiment = getOptionFlowRowSentiment(trade);

  switch (sentiment) {
    case OptionFlowRowSentiment.Bullish:
      return 'BULL';
    case OptionFlowRowSentiment.Bearish:
      return 'BEAR';
    case OptionFlowRowSentiment.Neutral:
    default:
      return 'NEUT';
  }
};
